/**
 * What counts as a HEIC/HEIF photo, and what a picker must accept to offer
 * one (#16099).
 *
 * Kept apart from `./convert-heic` on purpose: this module has no dynamic
 * import, so the server (`src/lib/notes/note-photo-image.ts`, which shares
 * the failure wording) can import it without its build growing a chunk for
 * the WASM decoder.
 */

/** MIME types a browser or OS reports for a HEIC/HEIF image. */
export const HEIC_MIME_TYPES: readonly string[] = [
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
]

/** File extensions of a HEIC/HEIF image, lowercase with the leading dot. */
export const HEIC_EXTENSIONS: readonly string[] = ['.heic', '.heif']

/**
 * Added to a file input's `accept` so macOS and Windows dialogs list HEIC
 * files as selectable rather than greying them out. The upload converts them
 * to JPEG before anything reaches the server.
 */
export const HEIC_ACCEPT: readonly string[] = [
  'image/heic',
  'image/heif',
  ...HEIC_EXTENSIONS,
]

/** What a person is told when a HEIC photo cannot be decoded. */
export const HEIC_CONVERSION_FAILED_MESSAGE =
  'This HEIC photo could not be converted. Save it as JPEG and try again.'

/**
 * What a person is told when the converter itself could not be fetched — a
 * network problem, not a problem with the photo, so "save it as JPEG" would
 * send them to fix the wrong thing.
 */
export const HEIC_CONVERTER_UNAVAILABLE_MESSAGE =
  'The photo converter could not load. Check your connection and try again.'

/**
 * Types a browser reports when it does not know the format — Chrome and
 * Firefox on macOS commonly give a HEIC an empty type.
 */
const UNKNOWN_TYPES = new Set(['', 'application/octet-stream'])

function hasHeicExtension(name: string): boolean {
  const lower = name.toLowerCase()
  return HEIC_EXTENSIONS.some(extension => lower.endsWith(extension))
}

/**
 * True for a file that should be converted before upload: a HEIC/HEIF MIME
 * type, or no useful type and a `.heic`/`.heif` name. A real non-HEIC type is
 * believed over the name.
 */
export function isHeicFile(file: Pick<File, 'name' | 'type'>): boolean {
  const type = file.type.toLowerCase()
  if (HEIC_MIME_TYPES.includes(type)) return true
  return UNKNOWN_TYPES.has(type) && hasHeicExtension(file.name)
}
